'use client';

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { GetCustomerNotificationsParams } from '@/entities/customer-notification/api/customer-notifications/types';
import { QueryApi } from '@/features/query/api';
import { applyLocalReadStateToList } from './is-news-unread';
import { useGuestNewsReadIds } from './news-read-storage';

type Options = GetCustomerNotificationsParams & {
    enabled?: boolean;
};

/** Waits for customer query before listing notifications so auth vs guest endpoint matches session. */
export const useCustomerNotificationsList = ({
    venueId,
    limit,
    offset = 0,
    enabled = true,
}: Options = {}) => {
    const customerQuery = useQuery(QueryApi.Customer.queries.Customer);
    const isLoggedIn = Boolean(customerQuery.data);
    const notificationsQuery = useQuery({
        ...QueryApi.CustomerNotifications.queries.List({
            venueId,
            limit,
            offset,
            authenticated: isLoggedIn,
        }),
        enabled: enabled && customerQuery.isFetched,
    });
    const readIds = useGuestNewsReadIds();
    const items = useMemo(
        () => applyLocalReadStateToList(notificationsQuery.data?.items ?? [], readIds),
        [notificationsQuery.data?.items, readIds],
    );

    return {
        customerQuery,
        notificationsQuery,
        isLoggedIn,
        items,
    };
};
